import { createPageAnalytics } from "common/analytics";

import { entryPageFromCurrentPage } from "@/analytics/entryPage";
import type {
  OnboardingAnswers,
  OnboardingCity,
  OnboardingStepName,
} from "@/features/onboarding/types";

const welcomeAnalytics = createPageAnalytics("welcome");
const tourPromptAnalytics = createPageAnalytics("tour_prompt");
const questionAnalytics = createPageAnalytics("onboarding_question");
const businessLocationAnalytics = createPageAnalytics(
  "business_location_confirm",
);
const createAccountPromptAnalytics = createPageAnalytics(
  "create_account_prompt",
);

/**
 * `step_name` for each question step — the same values native sends
 * (apps/native/src/analytics/onboardingAnalytics.ts), plus the desktop-only
 * "How did you hear about us?". The claim steps, "Let’s jump in!" and the
 * intro are absent on purpose: the plan has no question step name for them.
 */
const QUESTION_STEP_NAMES: Partial<Record<OnboardingStepName, string>> = {
  USER_TYPE: "use_type",
  AUDIENCE: "audience",
  ACCESS_NEEDS: "quick_search_filter",
  HEAR_ABOUT: "referral_source",
  CITY: "location",
  BUSINESS_CITY: "location",
};

export function isTrackedQuestionStep(step: OnboardingStepName): boolean {
  return step in QUESTION_STEP_NAMES;
}

/** A label in the plan's snake case, e.g. "Step-free access" → "step_free_access". */
function snakeCase(label: string | undefined): string {
  return (label ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function userTypeProp(answers: OnboardingAnswers): string {
  if (answers.userType === "individual") return "personal";
  if (answers.userType === "business") return "business";
  return "";
}

function audienceProp(answers: OnboardingAnswers): string {
  if (answers.audience === "myself") return "myself";
  if (answers.audience === "someone_i_care_for") return "someone_i_know";
  return "";
}

/** The desktop sheet has one consent page, so it is always step 1. */
export function trackWelcomeViewed(): void {
  welcomeAnalytics.track("page_view_page", {
    step_index: 1,
    previous_step_index: 0,
    step_name: "terms_and_conditions",
  });
}

/**
 * The "how it works" offer. On desktop it comes before the questions, so no
 * city has been picked yet and both city properties are empty.
 */
export function trackTourPromptViewed(): void {
  tourPromptAnalytics.track("page_view_page", {
    city_data_state: "",
    first_explore_city: "",
  });
}

export function trackQuestionStepViewed(
  step: OnboardingStepName,
  stepIndex: number,
  previousStepIndex: number,
  answers: OnboardingAnswers,
): void {
  const payload = {
    step_index: stepIndex,
    previous_step_index: previousStepIndex,
    step_name: QUESTION_STEP_NAMES[step] ?? "",
    use_type: userTypeProp(answers),
  };
  if (step === "BUSINESS_CITY") {
    businessLocationAnalytics.track("page_view_page", payload);
  } else {
    questionAnalytics.track("page_view_page", payload);
  }
}

/** The city is the last question on every path, so picking one completes them. */
export function trackQuestionsCompleted(
  step: OnboardingStepName,
  stepIndex: number,
  answers: OnboardingAnswers,
  city: OnboardingCity,
): void {
  const payload = {
    step_index: stepIndex,
    step_name: QUESTION_STEP_NAMES[step] ?? "",
    use_type: userTypeProp(answers),
    audience: audienceProp(answers),
    quick_search_filter: snakeCase(answers.accessNeeds[0]),
    first_explore_city: city.label || city.name,
    referral_source: snakeCase(answers.hearAbout?.label),
    referral_source_detail: answers.hearAbout?.otherText ?? "",
  };
  if (step === "BUSINESS_CITY") {
    businessLocationAnalytics.track("continue_action_button", payload);
  } else {
    questionAnalytics.track("continue_action_button", payload);
  }
}

export function trackQuestionsMinimised(stepIndex: number): void {
  questionAnalytics.track("minimise_icon", { step_index: stepIndex });
}

/** "Already have an account? Sign in" on step 1. */
export function trackOnboardingSignIn(): void {
  questionAnalytics.track("login_link_button", {
    entry_point: "onboarding",
    entry_page: entryPageFromCurrentPage(),
  });
}

/** "Sign up now" on "Let’s jump in!". */
export function trackOnboardingSignUp(): void {
  createAccountPromptAnalytics.track("sign_up_now_action_button", {
    entry_point: "onboarding",
    entry_page: entryPageFromCurrentPage(),
  });
}

export function trackBusinessLocationChange(): void {
  businessLocationAnalytics.track("change_link_button", {});
}
